/**
 * Pure-math helpers for the elevation-profile sampler.
 *
 * The polyline is treated as a sequence of XY line segments — Z is the
 * "elevation" we want to plot, never the projection axis. So all distance
 * tests are in the XY plane regardless of how steep individual segments are.
 *
 * Coordinate convention: project SRID, Z-up (enforced globally in
 * `app/src/components/Viewer.tsx`).
 */

export type PolylinePoint = [number, number, number];

export interface PolylineSegment {
  /** Start XY. */
  ax: number;
  ay: number;
  /** Start Z (elevation at the segment's first vertex). */
  az: number;
  /** End XY. */
  bx: number;
  by: number;
  /** End Z (elevation at the segment's second vertex). */
  bz: number;
  /** XY length of this segment (metres). */
  length: number;
  /** Cumulative XY length up to (and including the start of) this segment. */
  startChainage: number;
}

export interface PolylineXyAabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Build the per-segment metadata used by `projectPointToPolyline`. Returns an
 * empty array if the polyline has fewer than two distinct vertices.
 */
export function buildSegments(points: ReadonlyArray<PolylinePoint>): PolylineSegment[] {
  const segments: PolylineSegment[] = [];
  if (points.length < 2) return segments;
  let cum = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const ax = points[i][0];
    const ay = points[i][1];
    const az = points[i][2];
    const bx = points[i + 1][0];
    const by = points[i + 1][1];
    const bz = points[i + 1][2];
    const dx = bx - ax;
    const dy = by - ay;
    const length = Math.sqrt(dx * dx + dy * dy);
    // Drop degenerate segments — they would divide-by-zero in the projection
    // and contribute nothing to chainage.
    if (length < 1e-9) continue;
    segments.push({ ax, ay, az, bx, by, bz, length, startChainage: cum });
    cum += length;
  }
  return segments;
}

/** Total XY length of the polyline. */
export function totalLength(segments: ReadonlyArray<PolylineSegment>): number {
  if (segments.length === 0) return 0;
  const last = segments[segments.length - 1];
  return last.startChainage + last.length;
}

/**
 * XY axis-aligned bounding box of the polyline, expanded by `buffer` on every
 * side. Used as an octree node-culling rectangle: any point inside the buffer
 * corridor must lie inside this AABB.
 */
export function polylineXyAabb(
  points: ReadonlyArray<PolylinePoint>,
  buffer: number,
): PolylineXyAabb {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return {
    minX: minX - buffer,
    minY: minY - buffer,
    maxX: maxX + buffer,
    maxY: maxY + buffer,
  };
}

export interface PolylineProjection {
  /** Distance along the polyline from the first vertex (metres, XY only). */
  chainage: number;
  /** Perpendicular XY distance from the projected polyline (metres). */
  perpDist: number;
  /** Index of the segment the point projected onto. */
  segIndex: number;
}

/**
 * Project an (x, y) point onto the polyline. Iterates every segment and keeps
 * the one that yields the smallest perpendicular distance. The clamped
 * parameter `t ∈ [0, 1]` controls where along the segment the foot of the
 * perpendicular lies.
 *
 * Hot loop: the elevation-profile sampler calls this once per accepted point,
 * which can be millions of points for big clouds. No per-call allocation.
 */
export function projectPointToPolyline(
  px: number,
  py: number,
  segments: ReadonlyArray<PolylineSegment>,
): PolylineProjection | null {
  if (segments.length === 0) return null;

  let bestPerp = Infinity;
  let bestChainage = 0;
  let bestSeg = 0;

  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    const dx = s.bx - s.ax;
    const dy = s.by - s.ay;
    const lenSq = s.length * s.length;
    // Project (px, py) - a onto (b - a), clamped to [0, length].
    let t = ((px - s.ax) * dx + (py - s.ay) * dy) / lenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const fx = s.ax + t * dx;
    const fy = s.ay + t * dy;
    const ex = px - fx;
    const ey = py - fy;
    const perp = Math.sqrt(ex * ex + ey * ey);
    if (perp < bestPerp) {
      bestPerp = perp;
      bestChainage = s.startChainage + t * s.length;
      bestSeg = i;
    }
  }

  return { chainage: bestChainage, perpDist: bestPerp, segIndex: bestSeg };
}

/**
 * Inverse of the projection: given a chainage value, return the world XYZ
 * on the polyline (Z linearly interpolated between segment endpoints).
 * Used by the chart hover marker to position a 3D dot on the polyline at
 * the corresponding world location. Returns `null` if the polyline is
 * empty. Clamps `chainage` to the polyline range.
 */
export function chainageToWorld(
  chainage: number,
  segments: ReadonlyArray<PolylineSegment>,
): { x: number; y: number; z: number } | null {
  if (segments.length === 0) return null;
  const total = totalLength(segments);
  const c = chainage < 0 ? 0 : chainage > total ? total : chainage;

  // Find the segment that contains this chainage. Linear scan — segment
  // counts are tiny (<100) and this only fires on hover.
  let segIndex = segments.length - 1;
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    if (c <= s.startChainage + s.length) {
      segIndex = i;
      break;
    }
  }
  const s = segments[segIndex];
  const t = s.length > 0 ? (c - s.startChainage) / s.length : 0;
  return {
    x: s.ax + t * (s.bx - s.ax),
    y: s.ay + t * (s.by - s.ay),
    z: s.az + t * (s.bz - s.az),
  };
}
